import { Box } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import LeftPanel from "./LeftPanel";
import RightPanel from "./RightPanel";
import { useAuth } from "react-oidc-context";

type Activity = {
  admin_ref: string;
  direction: string;
  message: string;
  phone: string;
  timestamp: string;
  type: string;
};

type Contact = {
  phone: string;
  lastText: string;
  lastSeen: string;
  messages: Activity[];
};

type PlumberProfile = {
  phone: string;
  name?: string;
  wallet_balance?: number;
  total_earned?: number;
  address?: string;
  city?: string;
  state?: string;
  pin?: string;
  dob?: string;
  history?: Array<{
    date: string;
    code: string;
    type: "EARN" | "REDEMPTION";
    points: number;
  }>;
};

export default function Homepage() {
  const { user } = useAuth();
  const token = (user as any)?.id_token;

  const [activities, setActivities] = useState<Activity[]>([]);
  const [feedNextKey, setFeedNextKey] = useState<string | null>(null);
  const [feedLoadingNext, setFeedLoadingNext] = useState<boolean>(false);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [activePlumberProfile, setActivePlumberProfile] = useState<PlumberProfile | null>(null);
  const [plumberError, setPlumberError] = useState<string | null>(null);
  const [chatHistoryMessages, setChatHistoryMessages] = useState<Activity[]>([]);

  const fetchActivities = async (authToken: string, nextKey?: string | null) => {
    if (nextKey) setFeedLoadingNext(true);

    try {
      const url = new URL("https://e635rpl7hk.execute-api.us-east-1.amazonaws.com/admin/feed");
      if (nextKey) url.searchParams.set("nextKey", nextKey);

      const res = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();
      console.log('API response data:', data);

      // normalize items array
      const items: Activity[] = Array.isArray(data)
        ? data
        : Array.isArray((data as any).items)
        ? (data as any).items
        : Array.isArray((data as any).data)
        ? (data as any).data
        : [];

      const next = (data && ((data as any).nextKey ?? (data as any).next_key ?? (data as any).LastEvaluatedKey ?? null)) || null;

      // append new items to existing activities, de-duplicating by phone+timestamp
      setActivities((prev) => {
        const combined = [...prev, ...items];
        const map = new Map<string, Activity>();
        combined.forEach((a) => {
          const key = `${a.phone}::${a.timestamp}`;
          if (!map.has(key)) map.set(key, a);
        });
        return Array.from(map.values());
      });
      setFeedNextKey(next);
    } catch (err) {
      console.error('Error fetching activities:', err);
    } finally {
      if (nextKey) setFeedLoadingNext(false);
    }
  };

  const fetchPlumberProfile = async (phone: string, authToken: string) => {
    // clear any prior error while we fetch a new profile
    setPlumberError(null);

    try {
      const res = await fetch(
        `https://e635rpl7hk.execute-api.us-east-1.amazonaws.com/admin/plumber-detail?phone=${encodeURIComponent(phone)}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.status === 404) {
        console.log('Plumber profile returned 404 - unregistered');
        setActivePlumberProfile(null);
        setPlumberError('Unregistered');
        return;
      }

      if (!res.ok) {
        throw new Error(`HTTP error ${res.status}`);
      }

      const data = await res.json();
      console.log('Plumber profile data:', data);
      setActivePlumberProfile(data);
      setPlumberError(null);
    } catch (error) {
      console.error('Error fetching plumber profile:', error);
      setActivePlumberProfile(null);
      // do not propagate error state here; keep previous error cleared
      setPlumberError(null);
    }
  };

  const fetchChatHistory = async (phone: string, authToken: string) => {
    try {
      const res = await fetch(
        `https://e635rpl7hk.execute-api.us-east-1.amazonaws.com/admin/chat-history?phone=${encodeURIComponent(phone)}`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) {
        console.error('Chat history fetch failed', res.status);
        setChatHistoryMessages([]);
        return;
      }

      const data = await res.json();
      setChatHistoryMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching chat history:', err);
      setChatHistoryMessages([]);
    }
  };

  const handleRefresh = () => {
    if (!token) return;
    fetchActivities(token).then(() => {
      if (selectedPhone) {
        fetchPlumberProfile(selectedPhone, token);
        fetchChatHistory(selectedPhone, token);
      }
    });
  };

  useEffect(() => {
    if (!token) return;

    let mounted = true;

    fetchActivities(token);

    return () => {
      mounted = false;
    };
  }, [token]);

  const grouped = useMemo(() => {
    const map = new Map<string, Activity[]>();
    activities.forEach((a) => {
      const key = a.phone || "unknown";
      const arr = map.get(key) || [];
      arr.push(a);
      map.set(key, arr);
    });

    const contacts: Contact[] = Array.from(map.entries()).map(([phone, msgs]) => {
      msgs.sort((x, y) => (x.timestamp < y.timestamp ? 1 : -1));
      return {
        phone,
        lastText: msgs[0]?.message || msgs[0]?.type || "",
        lastSeen: msgs[0]?.timestamp || "",
        messages: msgs,
      };
    });

    // Sort contacts by latest timestamp (descending) so most recent at top
    contacts.sort((a, b) => {
      const aTime = a.lastSeen ? new Date(a.lastSeen).getTime() : 0;
      const bTime = b.lastSeen ? new Date(b.lastSeen).getTime() : 0;
      return bTime - aTime;
    });

    return contacts;
  }, [activities]);

  useEffect(() => {
    if (!selectedPhone && grouped.length > 0) {
      setSelectedPhone(grouped[0].phone);
    }
  }, [grouped, selectedPhone]);

  // Fetch plumber profile when a contact is selected
  useEffect(() => {
    if (!selectedPhone || !token) {
      setActivePlumberProfile(null);
      setPlumberError(null);
      setChatHistoryMessages([]);
      return;
    }

    fetchPlumberProfile(selectedPhone, token);
    fetchChatHistory(selectedPhone, token);
  }, [selectedPhone, token]);

  return (
    <Box display="flex" flexDirection="row" height="100vh" sx={{ overflow: 'hidden' }}>
      <LeftPanel
        contacts={grouped}
        selectedPhone={selectedPhone}
        onSelect={(phone: string) => {
          setSelectedPhone(phone);
          setPlumberError(null);
          setChatHistoryMessages([]);
        }}
        hasMore={!!feedNextKey}
        onLoadMore={() => { if (token && feedNextKey) fetchActivities(token, feedNextKey); }}
        loadingMore={feedLoadingNext}
      />
      <Box
        sx={{
          border: ".05px solid #2f3b44",
        }}
      />
      <RightPanel
        contact={grouped.find((c) => c.phone === selectedPhone) || null}
        plumberProfile={activePlumberProfile}
        plumberError={plumberError}
        chatHistory={chatHistoryMessages}
        onRefresh={handleRefresh}
      />
    </Box>
  );
}
